import { useAuth0 } from '@auth0/auth0-react';
import { MouseEvent, SyntheticEvent } from 'react';
import { getGroupByTitle } from '@/apis/groups';
import { GroupRecordResult } from '@components/GroupsFetchAutocomplete';
import { TagRecordResult } from '@components/TagsFetchAutocomplete';

type GroupTagHandlersParams = {
  accordionStartingState: boolean[];
  groupAddEntities: GroupRecordResult[];
  setGroupAddEntities: (v: GroupRecordResult[]) => void;
  groupRemoveEntities: GroupRecordResult[];
  setGroupRemoveEntities: (v: GroupRecordResult[]) => void;
  setNewOrExistingGroup: (v: 'existing' | 'new') => void;
  newGroupNames: GroupRecordResult[];
  setNewGroupNames: (v: GroupRecordResult[]) => void;
  setGroupOverlap: (v: boolean) => void;
  groupNamesClash: string[];
  setGroupNamesClash: (v: string[]) => void;
  tagAddEntities: TagRecordResult[];
  setTagAddEntities: (v: TagRecordResult[]) => void;
  tagRemoveEntities: TagRecordResult[];
  setTagRemoveEntities: (v: TagRecordResult[]) => void;
  setTagOverlap: (v: boolean) => void;
  setAccordionExpansion: (v: boolean[]) => void;
  setUpdateRocketStatus: (v: string | null) => void;
  setElectionValue: (v: { id: number; label: string } | null) => void;
  setTaskKindType: (v: string | null) => void;
  setLevelOfInterest: (v: string | null) => void;
  setNotes: (v: string) => void;
  setUpdatePathwayStep: (v: { label: string; id: number } | null) => void;
  setUpdatePathwayOption: (v: { label: string; id: number } | null) => void;
};

export function useOtherActionsPanelFormHandlers({
  accordionStartingState: ACCORDION_STARTING_STATE,
  groupAddEntities,
  setGroupAddEntities,
  groupRemoveEntities,
  setGroupRemoveEntities,
  setNewOrExistingGroup,
  newGroupNames,
  setNewGroupNames,
  setGroupOverlap,
  groupNamesClash,
  setGroupNamesClash,
  tagAddEntities,
  setTagAddEntities,
  tagRemoveEntities,
  setTagRemoveEntities,
  setTagOverlap,
  setAccordionExpansion,
  setUpdateRocketStatus,
  setElectionValue,
  setTaskKindType,
  setLevelOfInterest,
  setNotes,
  setUpdatePathwayStep,
  setUpdatePathwayOption,
}: GroupTagHandlersParams) {
  const { getAccessTokenSilently } = useAuth0();

  const resetInternalState = () => {
    setGroupAddEntities([]);
    setGroupRemoveEntities([]);
    setGroupOverlap(false);
    setNewOrExistingGroup('existing');
    setNewGroupNames([]);
    setGroupNamesClash([]);
    setTagAddEntities([]);
    setTagRemoveEntities([]);
    setTagOverlap(false);
    setUpdateRocketStatus(null);
    setElectionValue(null);
    setTaskKindType(null);
    setLevelOfInterest(null);
    setNotes('');
    setUpdatePathwayStep(null);
    setUpdatePathwayOption(null);
    setAccordionExpansion(ACCORDION_STARTING_STATE);
  };

  const handleNewOrExistingGroupChange = (
    _event: MouseEvent<HTMLElement>,
    newValue: 'existing' | 'new'
  ) => {
    if (newValue) {
      setNewOrExistingGroup(newValue);
      if (newValue === 'existing') {
        setNewGroupNames([]);
        setGroupNamesClash([]);
      } else {
        setGroupAddEntities([]);
        setGroupOverlap(false);
      }
    }
  };

  const handleAutocompleteNewGroupChange = async (newValue: GroupRecordResult[]) => {
    if (newGroupNames.length > newValue.length) {
      if (newValue.length === 0) {
        setGroupNamesClash([]);
      } else {
        let removedItem = '';
        for (let i = 0; i < newValue.length; i++) {
          if (newGroupNames[i].label !== newValue[i].label) {
            removedItem = newGroupNames[i].label;
            break;
          }
        }
        if (removedItem === '') {
          removedItem = newGroupNames[newGroupNames.length - 1].label;
        }
        setGroupNamesClash(groupNamesClash.filter((x) => x !== removedItem));
      }
    } else {
      const token = await getAccessTokenSilently();
      const abortController = new AbortController();
      const isGroupThere = await getGroupByTitle(
        token,
        newValue[newValue.length - 1].label,
        abortController.signal
      );
      if (isGroupThere.length > 0) {
        setGroupNamesClash([...groupNamesClash, isGroupThere[0].title]);
      }
    }
    setNewGroupNames(newValue);
  };

  const handleAutocompleteAddGroupChange = (newValue: GroupRecordResult[]) => {
    setGroupAddEntities(newValue);
    const groupAddEntityIds = new Set(newValue.map((x: GroupRecordResult) => x.id));
    const groupRemoveEntityIds = groupRemoveEntities.map((x: GroupRecordResult) => x.id);
    const intersection = new Set();
    for (const elem of groupRemoveEntityIds) {
      if (groupAddEntityIds.has(elem)) intersection.add(elem);
    }
    setGroupOverlap(intersection.size !== 0);
  };

  const handleAutocompleteAddTagChange = (newValue: TagRecordResult[]) => {
    setTagAddEntities(newValue);
    const tagAddEntityIds = new Set(newValue.map((x: TagRecordResult) => x.id));
    const tagRemoveEntityIds = tagRemoveEntities.map((x: TagRecordResult) => x.id);
    const intersection = new Set();
    for (const elem of tagRemoveEntityIds) {
      if (tagAddEntityIds.has(elem)) intersection.add(elem);
    }
    setTagOverlap(intersection.size !== 0);
  };

  const handleAutocompleteRemoveTagChange = (newValue: TagRecordResult[]) => {
    setTagRemoveEntities(newValue);
    const tagRemoveEntityIds = new Set(newValue.map((x: TagRecordResult) => x.id));
    const tagAddEntityIds = tagAddEntities.map((x: TagRecordResult) => x.id);
    const intersection = new Set();
    for (const elem of tagAddEntityIds) {
      if (tagRemoveEntityIds.has(elem)) intersection.add(elem);
    }
    setTagOverlap(intersection.size !== 0);
  };

  const handleAutocompleteRemoveGroupChange = (newValue: GroupRecordResult[]) => {
    setGroupRemoveEntities(newValue);
    const groupAddEntityIds = new Set(groupAddEntities.map((x: GroupRecordResult) => x.id));
    const groupRemoveEntityIds = newValue.map((x: GroupRecordResult) => x.id);
    const intersection = new Set();
    for (const elem of groupRemoveEntityIds) {
      if (groupAddEntityIds.has(elem)) intersection.add(elem);
    }
    setGroupOverlap(intersection.size !== 0);
  };

  const handleElectionValueChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: { id: number; label: string } | null
  ) => {
    setElectionValue(newValue);
    if (newValue === null) {
      setTaskKindType(null);
      setLevelOfInterest(null);
      setNotes('');
    }
  };

  const handleChangeNotes = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };

  return {
    resetInternalState,
    handleNewOrExistingGroupChange,
    handleAutocompleteNewGroupChange,
    handleAutocompleteAddGroupChange,
    handleAutocompleteAddTagChange,
    handleAutocompleteRemoveTagChange,
    handleAutocompleteRemoveGroupChange,
    handleElectionValueChange,
    handleChangeNotes,
  };
}
